<template>
  <b-card style="min-height: 600px; max-width: 500px; overflow: auto" 
          :class="{'jaguar-bg': Brand == 'Jaguar', 'landrover-bg': Brand == 'Land Rover', 'text-white': Brand == 'Land Rover'}">
    <img  class="mx-auto img-fluid jlrlogo" 
          :src="`/static/img/${logoName}`" />

    <b-card-text class="msm-title" style="font-size:1.2rem;">
      {{ this.Title ? this.Title : $t('Webportal.CX.Margin.MarginCardTitle') }}
    </b-card-text>
      
    <!-- Icon only visible in "lg"-large mode or in classic view (lg and sm in one branded pane) -->
    <b-icon v-if="!mode || mode == 'lg'"
            class="help-icon clickable" 
            icon="question-circle" 
            @click="toggleMarginHelp()" />

    <b-card-text v-if="!loadingData">

      <!-- Large part of this control. Will be shown in undef and "lg"-large mode -->
      <MarginKpiGroup v-if="!mode || mode == 'lg'"
                      :clientLocationId="ClientLocationId"
                      :brand="Brand"
                      :kpiGroup="largeKpiGroup" 
                      :openPopup="openPopup" 
                      :colorRange="colorRange"
                      :isHealingDeactivated="false"
                      :currentWave="currentWave" />

      <!-- Small part of this control. Will be shown in undef and "sm"-small mode -->
      <MarginKpiGroup v-if="!mode || mode == 'sm'"
                      :clientLocationId="ClientLocationId"
                      :kpiGroup="smallKpiGroup" 
                      :brand="Brand"
                      :openPopup="openPopup" 
                      :colorRange="colorRange"
                      :isHealingDeactivated="true"
                      :currentWave="currentWave" />
    </b-card-text>

    <MarginKpiPopup v-if="showPopup"
                    :ColorRange="colorRange"
                    :ClientLocationId="ClientLocationId"
                    :Kpi="selectedKpi"
                    :WaveName="selectedWave"
                    @close="hidePopup()" />
  </b-card>
</template>

<script>
const FORMATTING = Object.freeze({
  Default: 0,           /* xx% */
  None: 1,              /* xx */
  Time: 2,              /* xx:yy h */
  Time_Dynamic: 3,      /* xx Min || xx:yy h */
  Minutes: 4,           /* xx Min */
  Hours: 5,             /* < 1 h || xx h */
});

import DashboardService from "@/services/DashboardService";
import MarginKpiPopup from "./MarginKpiPopup.vue";
import MarginKpiGroup from './MarginKpiGroup.vue';
import MarginKpiStatusIndicator from './MarginKpiStatusIndicator.vue';
import EventBus from "@/services/EventBus";
import MarginKpiMixin from '@/mixins/MarginKpiMixin';
import { i18n } from "@/i18n";

export default {
  components: {
    DashboardService,
    MarginKpiPopup,
    MarginKpiGroup,
    MarginKpiStatusIndicator
  },

  mixins: [ MarginKpiMixin ],

  props: {
    colorRange:       Array,
    Title:            { type: String, default: "Margin Evaluation" },
    MarginKPIGroups:  { type: Array, default: [] },
    ClientLocationId: Number,
    Brand:            String,
    mode:             { type: String, default: undefined }
  },

  data() {
    return {
      posDivider: this.getRandom(2, 8),
      posOffset: this.getRandom(-150, 150),

      KpiIds: [],
      showReview: false,
      loadingData: false,
      selectedKpi: null,
      selectedWave: null,
      // currentWave: null,
      showPopup: false,
      waveTotalValues: [],
      showHelp: false,
      showHelpOnlyLegend: true,
    };
  },

  watch: {
    MarginKPIGroups: {
      immediate: true,
      handler(val, oldVal) {
        this.modifyData();
      },
    },
  },

  mounted() {
    EventBus.$on("refresh-margin-card", this.modifyData);
    EventBus.$on("toggle-help", (brand) => { 
      if(this.Brand == brand) {
        this.showHelpOnlyLegend = false;
        this.showHelp = !this.showHelp;
      }
    });
  },

  created() {
    this.calculateCurrentWaveForHealing();
  },

  computed: {
    logoName: function(){
      return this.Brand == "Jaguar" ? "jaguar-logo.png" : "landrover-logo.png";
    },

    smallKpiGroup() {
      return this.MarginKPIGroups[1];
    },
    largeKpiGroup() {
      return this.MarginKPIGroups[0];
    } 
  },

  methods: {
    modifyData() {
      this.loadingData = true;

      this.MarginKPIGroups.forEach(kpiGroup => {
        kpiGroup.Items.forEach(kpi => {
          this.KpiIds.push(kpi.SurveyKpiIds)
        });
      });
      
      let self = this;
      Promise.all([
        this.getKpisByYear().catch(e => _), 
        this.getWaves().catch(e => _)]
      ).then(() => {
        this.calculateWaveTotalResults();
        this.loadingData = false;
      }).then(() => {
        self.$forceUpdate();
      });
    },

    getWaves() {
      var promises = []
      this.MarginKPIGroups.forEach(kpiGroup => {
        var waveIds = kpiGroup.SurveyWave.map(wave => wave.Id);
        if(waveIds.length > 0) {
          promises.push(DashboardService.GetSurveyWavesOfSameSurvey(waveIds.toString())
            .then((res) => {
            kpiGroup.Waves = res.data;
              kpiGroup.Items.forEach(kpi => {
                kpi.Waves = res.data;
              });
            })
            .catch((error) => {
              console.error(error);
            }))
        }
      })
      return Promise.all(promises)
    },
    getKpisByYear() {
      const kpiids = this.KpiIds.filter(id => id != "");
      if(kpiids.length == 0 ) 
        return Promise.reject("kpis empty");
      //TODO: Exchange 2022 with the selected year  
      return DashboardService.GetSurveyKpisByYear(this.KpiIds.toString(), i18n.locale, this.ClientLocationId, 2022)
        .then(res => {
          return res.data == "" ? { data: [] } : res
        })
        .then(res => {
          res.data.forEach(kpiResult => {
            this.MarginKPIGroups.forEach(kpiGroup => {
              kpiGroup.Items.forEach(kpi => {
                if( kpi.Code == kpiResult.KpiCode) {
                  kpi.SurveyWave.push({
                    "CalculationMode": kpiResult.CalculationMode,
                    "SurveyWaveIds": kpiResult.SurveyWaveIds,
                    "Name": kpiResult.SurveyWaveName,
                    "PointsReached": kpiResult.PointsReached,
                    "PointsMax": kpiResult.PointsMax,
                    "Result": kpiResult.Result,
                    "Scenario": kpiResult.ScenarioName,
                    "KpiGroupName": kpiResult.KpiGroupName,
                    "Accomplished": kpiResult.Result == 1
                  })
                }
              })
            })
          })
        })
    },
    calculateWaveTotalResults() {
      for(var groupIndex = 0; groupIndex < this.MarginKPIGroups.length; groupIndex++) {
        for(var waveIndex = 0; waveIndex < this.MarginKPIGroups[groupIndex].Waves.length; waveIndex ++) {
          let count = null;
          let correctKpiCounter = null;
          for (var itemIndex = 0; itemIndex < this.MarginKPIGroups[groupIndex].Items.length; itemIndex++){
            var x = this.MarginKPIGroups[groupIndex].Items[itemIndex].SurveyWave.find(y => y.Name == this.MarginKPIGroups[groupIndex].Waves[waveIndex].NameShort);
            if(x) {
              count++;
              correctKpiCounter += x.Result == 1 ? 1 : 0;
            };
          };
          if(count != null) {
            this.MarginKPIGroups[groupIndex].Waves[waveIndex].Result = (correctKpiCounter / count);
          } else {
            this.MarginKPIGroups[groupIndex].Waves[waveIndex].Result = null
          }
        }
      }
    },
    getColor(value) {
      if (!this.colorRange) return null;

      return this.colorRange.find(
        (thres) => thres.Minimum <= value && thres.Maximum >= value
      )?.Color;
    },
    getMehColor() {
      return this.colorRange.find((thres) => thres.Smiley == "meh")?.Color;
    },
    openPopup(kpi, wave) {
      if(kpi.SurveyWave.find(x => x.Name == wave)) {
        if (this.selectedKpi == kpi && this.selectedWave == wave) {
          this.hidePopup();
        } else {
          kpi.ShowPopup = true; 
          this.selectedKpi = kpi;
          this.selectedWave = wave;
          this.showPopup = true; 
        } 
      } else {
        this.hidePopup();
      }
    },
    hidePopup() {
      this.selectedKpi = null;
      this.selectedWave = null;
      this.showPopup = false;
    },
    getRandom(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1) + min);
    },
    toggleMarginHelp() {
      EventBus.$emit("toggle-margin-help");
    },
  },
};
</script>

<style lang="scss" scoped>
.msm-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: unset!important;
  padding: 0.25rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>